/**
 * Profile Detail — pure update function.
 *
 * Elm-ish doctrine: pure; no API calls; no hooks.
 *
 * R3 (correction ACT): `CtaEnabledChanged` flips the
 * `messageCtaEnabled` flag declaratively.
 *
 * R8 (correction ACT): `MessageCtaClicked` returns the
 * `OpenPersonalChat` effect directly. The harness is no longer
 * allowed to fire effects based on React state; pure-update tests
 * can now assert the exact effect emitted by the click message.
 */

import {
  initialDetailModel,
  type ProfileDetailModel,
  type ProfileVariant,
} from './model';
import type { ProfileDetailMsg } from './messages';

export type ProfileDetailEffect =
  | { readonly type: 'LoadDetail'; readonly profileId: string; readonly variant: ProfileVariant }
  | {
      readonly type: 'OpenPersonalChat';
      readonly targetProfileId: string;
      /**
       * R8 (correction ACT): the navigation callback is supplied at
       * effect-run time by the harness, NOT at message-dispatch time
       * by the React layer. The pure reducer never closes over
       * navigation. The effect payload carries only data; the
       * `EffectContext` carries the side-effect closures.
       *
       * Bucket C (symmetric entry): the field is now targetProfileId;
       * the seam is symmetric for both directions.
       */
    };

export interface UpdateResult {
  readonly model: ProfileDetailModel;
  readonly effects: readonly ProfileDetailEffect[];
}

export function update(
  model: ProfileDetailModel,
  msg: ProfileDetailMsg
): UpdateResult {
  switch (msg.type) {
    case 'Started':
      if (model.status === 'loading' || model.status === 'ready') {
        return { model, effects: [] };
      }
      return {
        model: { ...model, status: 'loading' },
        effects: [{ type: 'LoadDetail', profileId: model.profileId, variant: model.variant }],
      };

    case 'DetailLoaded':
      return {
        model: {
          ...model,
          status: 'ready',
          detail: msg.detail,
          // CORRECTION03 (B-2.B): surface photo-protocol violations
          // loudly (visible in the view) rather than silently
          // dropping malformed photos. The valid photos are still
          // adopted and rendered.
          photoProtocolViolations: msg.photoProtocolViolations,
        },
        effects: [],
      };

    case 'DetailLoadFailed':
      return {
        model: {
          ...model,
          status: 'error',
          detail: null,
        },
        effects: [],
      };

    case 'RetryRequested':
      return {
        model: { ...model, status: 'loading', messageCtaError: null },
        effects: [{ type: 'LoadDetail', profileId: model.profileId, variant: model.variant }],
      };

    case 'CtaEnabledChanged':
      if (model.messageCtaEnabled === msg.enabled) {
        return { model, effects: [] };
      }
      return {
        model: { ...model, messageCtaEnabled: msg.enabled },
        effects: [],
      };

    case 'MessageCtaClicked':
      if (!model.detail) {
        return { model, effects: [] };
      }
      if (!model.messageCtaEnabled) {
        // The CTA is not supported for this viewer's registration
        // kind (per R3). The button should not be visible in the
        // first place, but defense-in-depth: emit no effect.
        return { model, effects: [] };
      }
      // R8 (correction ACT): the pure reducer now declares the
      // effect directly. The harness runs pending effects as data,
      // and the EffectContext (which contains the navigation
      // callback) supplies the side-effect closures. Pure-update
      // tests can now assert the exact effect shape.
      return {
        model: {
          ...model,
          messageCtaLoading: true,
          messageCtaError: null,
        },
        effects: [
          {
            type: 'OpenPersonalChat',
            targetProfileId: model.detail.id,
          },
        ],
      };

    case 'MessageCtaSucceeded':
      return {
        model: {
          ...model,
          messageCtaLoading: false,
          messageCtaError: null,
        },
        effects: [],
      };

    case 'MessageCtaFailed':
      return {
        model: {
          ...model,
          messageCtaLoading: false,
          messageCtaError: msg.error,
        },
        effects: [],
      };

    case 'MessageCtaCleared':
      return {
        model: { ...model, messageCtaError: null },
        effects: [],
      };

    default: {
      const _exhaustive: never = msg;
      return { model, effects: [] };
    }
  }
}

export { initialDetailModel };